import type { BoardState, Point } from './board'

// Nominal NFHS high-school dimensions in feet. One isotropic scale is used for
// every marking; switching court length never compresses a half-court drawing.
// Reference: NFHS 2025-26 Basketball Court Diagram (KHSAA publication).
export const HIGH_SCHOOL_COURT = {
  width: 50, length: 84, halfLength: 42,
  laneWidth: 12, freeThrowDistance: 19, freeThrowRadius: 6,
  basketDistance: 5.25, rimRadius: .75,
  backboardDistance: 4, backboardWidth: 6,
  threePointRadius: 19.75, centerRadius: 6, lineWidth: 2 / 12,
} as const

export const COURT_SCALE = 15
export const COURT_MARGIN = 5
// Six feet of drawing space around the existing model. Keep the model origin
// unchanged so adding an apron does not move any previously saved play.
export const OUT_OF_BOUNDS_SPACE = 90
export const MARKER_CLEARANCE = 44
export const courtUnits = (feet: number) => feet * COURT_SCALE
export const courtPosition = (feet: number) => COURT_MARGIN + courtUnits(feet)

export function courtDimensions(fullCourt = false) {
  return { width: courtUnits(HIGH_SCHOOL_COURT.width) + COURT_MARGIN * 2,
    height: courtUnits(fullCourt ? HIGH_SCHOOL_COURT.length : HIGH_SCHOOL_COURT.halfLength) + COURT_MARGIN * 2 }
}

export function courtViewport(fullCourt = false) {
  const size = courtDimensions(fullCourt)
  return { x: -OUT_OF_BOUNDS_SPACE, y: -OUT_OF_BOUNDS_SPACE,
    width: size.width + OUT_OF_BOUNDS_SPACE * 2, height: size.height + OUT_OF_BOUNDS_SPACE * 2 }
}

export function drawingViewport(board: BoardState) {
  const size = courtDimensions(!!board.fullCourt), base = courtViewport(!!board.fullCourt)
  const points: Point[] = [...board.players, ...board.strokes.flatMap(s => s.points),
    ...board.play?.initialPlayers || [], ...board.play?.steps.flatMap(s => s.movements.flatMap(m => m.points)) || []]
  if (board.looseBall) points.push(board.looseBall)
  let factor = 1
  for (const p of points) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue
    factor = Math.max(factor, (Math.abs(p.x*size.width-size.width/2)+MARKER_CLEARANCE)/(base.width/2),
      (Math.abs(p.y*size.height-size.height/2)+MARKER_CLEARANCE)/(base.height/2))
  }
  return { x: (size.width-base.width*factor)/2, y: (size.height-base.height*factor)/2,
    width: base.width*factor, height: base.height*factor }
}

export function endGeometry() {
  const c = HIGH_SCHOOL_COURT
  const centerX = courtPosition(c.width / 2)
  return {
    centerX, baseline: COURT_MARGIN,
    laneLeft: courtPosition((c.width-c.laneWidth)/2), laneRight: courtPosition((c.width+c.laneWidth)/2),
    freeThrowY: courtPosition(c.freeThrowDistance), freeThrowRadius: courtUnits(c.freeThrowRadius),
    basketY: courtPosition(c.basketDistance), rimRadius: courtUnits(c.rimRadius),
    backboardY: courtPosition(c.backboardDistance), backboardHalfWidth: courtUnits(c.backboardWidth/2),
    threePointRadius: courtUnits(c.threePointRadius),
    threePointLeft: courtPosition(c.width/2-c.threePointRadius), threePointRight: courtPosition(c.width/2+c.threePointRadius),
  }
}

export function semicirclePath(cx: number, cy: number, radius: number, towardCenter = true) {
  return `M ${cx-radius} ${cy} A ${radius} ${radius} 0 0 ${towardCenter ? 0 : 1} ${cx+radius} ${cy}`
}
